import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, ArrowDownUp, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { callAdminGetAuditLogs } from '../../services/admin';

type SyncLog = {
  id: string;
  eventType?: string;
  outcome?: string;
  category?: string;
  actor?: Record<string, unknown> | null;
  target?: Record<string, unknown> | null;
  source?: Record<string, unknown> | null;
  details?: Record<string, unknown> | null;
  createdAt?: any;
};

const statusPill = (value: string) => {
  const v = (value || '').toLowerCase();
  if (v === 'success' || v === 'processed') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (v === 'error' || v === 'rejected') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  if (v === 'received') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
  return 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300';
};

const eventLabel = (eventType: string): { short: string; description: string } => {
  const map: Record<string, { short: string; description: string }> = {
    'sync.entitlement_change.received': { short: 'Entitlement Received', description: 'Entitlement change trigger fired' },
    'sync.entitlement_change.synced': { short: 'Entitlement Synced', description: 'Successfully synced entitlement to product' },
    'sync.entitlement_change.error': { short: 'Entitlement Sync Error', description: 'Failed to sync entitlement to product' },
    'sync.user_subscription.upserted': { short: 'User Subscription', description: 'Product user subscription record created/updated' },
    'sync.user_identity.password_synced': { short: 'Password Synced', description: 'User password synced to product auth' },
    'sync.user_identity.update_error': { short: 'Identity Update Error', description: 'Failed to update user identity in product auth' },
    'sync.clinic_bootstrap.success': { short: 'Clinic Created', description: 'Clinic and membership bootstrapped successfully' },
    'sync.clinic_bootstrap.skipped': { short: 'Clinic Skipped', description: 'Clinic bootstrap skipped (inactive entitlement)' },
    'sync.clinic_bootstrap.error': { short: 'Clinic Error', description: 'Clinic bootstrap failed' },
  };
  return map[eventType] || { short: eventType || '—', description: '' };
};

const tsToMs = (ts: any) => {
  if (!ts) return null;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts?._seconds === 'number') return ts._seconds * 1000;
  const parsed = new Date(ts).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const formatDate = (ts: any) => {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getText = (obj: Record<string, unknown> | null | undefined, key: string): string => {
  const value = obj?.[key];
  return value == null ? '' : String(value);
};

const AdminVetAxisSyncLogs: React.FC = () => {
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(100);
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = async (nextLimit = limit) => {
    setLoading(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = { category: 'sync', limit: nextLimit };
      if (outcomeFilter !== 'all') payload.outcome = outcomeFilter;
      const data = await callAdminGetAuditLogs(payload as any);
      setLogs(data.logs || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load sync logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = search
    ? logs.filter((log) => {
        const q = search.toLowerCase();
        return (
          (log.eventType || '').toLowerCase().includes(q) ||
          (log.outcome || '').toLowerCase().includes(q) ||
          getText(log.target, 'uid').toLowerCase().includes(q) ||
          getText(log.target, 'email').toLowerCase().includes(q) ||
          getText(log.target, 'clinicId').toLowerCase().includes(q) ||
          getText(log.details, 'reason').toLowerCase().includes(q) ||
          getText(log.details, 'email').toLowerCase().includes(q) ||
          getText(log.details, 'clinicName').toLowerCase().includes(q)
        );
      })
    : logs;

  const errorCount = logs.filter((l) => l.outcome === 'error').length;
  const successCount = logs.filter((l) => l.outcome === 'success').length;
  const skippedCount = logs.filter((l) => (l.eventType || '').includes('.skipped')).length;

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin/vetaxis" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to VetAxis Ops
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <ArrowDownUp className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Sync Logs</h1>
              <p className="text-slate-600 dark:text-gray-400">Entitlement sync, user identity resolution, clinic bootstrap, and password sync events</p>
            </div>
          </div>
        </motion.div>

        {/* Stats strip */}
        {!loading && logs.length > 0 && (
          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <div className="glass-effect rounded-xl p-4 shadow-glass text-center">
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{logs.length}</div>
              <div className="text-xs text-slate-500 dark:text-gray-400">Total Events</div>
            </div>
            <div className="glass-effect rounded-xl p-4 shadow-glass text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{successCount}</div>
              <div className="text-xs text-slate-500 dark:text-gray-400">Successful</div>
            </div>
            <div className="glass-effect rounded-xl p-4 shadow-glass text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{errorCount}</div>
              <div className="text-xs text-slate-500 dark:text-gray-400">Errors</div>
            </div>
            <div className="glass-effect rounded-xl p-4 shadow-glass text-center">
              <div className="text-2xl font-bold text-slate-500 dark:text-gray-400">{skippedCount}</div>
              <div className="text-xs text-slate-500 dark:text-gray-400">Skipped</div>
            </div>
          </motion.div>
        )}

        {/* Controls */}
        <motion.div className="glass-effect rounded-2xl p-4 shadow-glass mb-6" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <label className="text-sm text-slate-700 dark:text-gray-300 inline-flex items-center gap-2">
              <span>Limit</span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="px-2 py-1 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </label>
            <label className="text-sm text-slate-700 dark:text-gray-300 inline-flex items-center gap-2">
              <span>Outcome</span>
              <select
                value={outcomeFilter}
                onChange={(e) => setOutcomeFilter(e.target.value)}
                className="px-2 py-1 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50"
              >
                <option value="all">All</option>
                <option value="success">Success</option>
                <option value="error">Error</option>
                <option value="received">Received</option>
                <option value="info">Info</option>
              </select>
            </label>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search UID, email, clinic..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm"
              />
            </div>
            <button
              onClick={() => void load(limit)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-gray-700 text-sm text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </motion.div>

        {error && <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>}

        {/* Table */}
        <motion.div className="glass-effect rounded-2xl p-4 shadow-glass" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          {loading ? (
            <p className="text-sm text-slate-500 dark:text-gray-400">Loading sync logs...</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-gray-400">No sync logs found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 dark:text-gray-400 border-b border-slate-200 dark:border-gray-700">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Event</th>
                    <th className="py-2 pr-3">Outcome</th>
                    <th className="py-2 pr-3">User</th>
                    <th className="py-2 pr-3">Product</th>
                    <th className="py-2 pr-3">Summary</th>
                    <th className="py-2 pr-1 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((log) => {
                    const label = eventLabel(log.eventType || '');
                    const uid = getText(log.target, 'uid') || getText(log.target, 'suiteUid') || '—';
                    const email = getText(log.target, 'email') || getText(log.details, 'email') || '';
                    const productId = getText(log.target, 'productId') || '—';
                    const clinicId = getText(log.target, 'clinicId') || getText(log.details, 'clinicId') || '';
                    const reason = getText(log.details, 'reason') || '';
                    const plan = getText(log.details, 'plan') || getText(log.details, 'planId') || '';
                    const status = getText(log.details, 'status') || '';
                    const expanded = expandedId === log.id;

                    let summary = '';
                    if (reason) summary = reason;
                    else if (clinicId) summary = `Clinic: ${clinicId}`;
                    else if (plan && status) summary = `${plan} / ${status}`;
                    else if (plan) summary = plan;
                    else if (status) summary = status;

                    return (
                      <React.Fragment key={log.id}>
                        <tr className="border-b border-slate-100 dark:border-gray-800 hover:bg-slate-50/50 dark:hover:bg-gray-800/30">
                          <td className="py-2 pr-3 whitespace-nowrap">{formatDate(log.createdAt)}</td>
                          <td className="py-2 pr-3">
                            <div className="font-medium text-slate-800 dark:text-gray-200">{label.short}</div>
                            <div className="text-xs text-slate-400 dark:text-gray-500">{label.description}</div>
                          </td>
                          <td className="py-2 pr-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${statusPill(log.outcome || 'info')}`}>{log.outcome || 'info'}</span>
                          </td>
                          <td className="py-2 pr-3">
                            <div className="font-mono text-xs">{uid}</div>
                            {email && <div className="text-xs text-slate-400 dark:text-gray-500">{email}</div>}
                          </td>
                          <td className="py-2 pr-3 font-mono text-xs">{productId}</td>
                          <td className="py-2 pr-3 text-slate-700 dark:text-gray-300 max-w-xs truncate" title={summary}>{summary || '—'}</td>
                          <td className="py-2 pr-1">
                            <button
                              onClick={() => setExpandedId(expanded ? null : log.id)}
                              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-gray-700"
                            >
                              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="border-b border-slate-100 dark:border-gray-800">
                            <td colSpan={7} className="py-3 px-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                {log.target && (
                                  <div>
                                    <div className="font-semibold text-slate-600 dark:text-gray-300 mb-1">Target</div>
                                    <pre className="bg-slate-50 dark:bg-gray-900 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap text-slate-700 dark:text-gray-300">
                                      {JSON.stringify(log.target, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {log.details && (
                                  <div>
                                    <div className="font-semibold text-slate-600 dark:text-gray-300 mb-1">Details</div>
                                    <pre className="bg-slate-50 dark:bg-gray-900 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap text-slate-700 dark:text-gray-300">
                                      {JSON.stringify(log.details, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {log.source && (
                                  <div>
                                    <div className="font-semibold text-slate-600 dark:text-gray-300 mb-1">Source</div>
                                    <pre className="bg-slate-50 dark:bg-gray-900 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap text-slate-700 dark:text-gray-300">
                                      {JSON.stringify(log.source, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminVetAxisSyncLogs;
